import { IconType } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { ComplianceCaseResolution, ComplianceCaseStatus } from "~/graphql/supergraphEnums";

type DisplayInfo = {
  displayText: string;
  iconType?: IconType;
  iconColor?: Colors;
};

const isUnspecifiedResolution = (
  resolution: ComplianceCaseResolution | string | null | undefined,
): boolean =>
  !resolution || resolution === ComplianceCaseResolution.Unknown || resolution === "--";

const isInProgressStatus = (status?: ComplianceCaseStatus | string | null): boolean =>
  status === ComplianceCaseStatus.InProgress || status === "In progress";

/** Internal escalations (entity/client onboarding) never involve a GP, so their verdicts read as internal. */
const INTERNAL_DISPLAY_TEXT: Partial<Record<ComplianceCaseResolution, string>> = {
  [ComplianceCaseResolution.ProposedEscalation]: "Proposed escalation",
  [ComplianceCaseResolution.Escalated]: "Escalated",
  [ComplianceCaseResolution.ProposedAcceptance]: "Internal verdict rendered",
  [ComplianceCaseResolution.ProposedRejection]: "Internal verdict rendered",
  [ComplianceCaseResolution.Accepted]: "Approved by Internal",
  [ComplianceCaseResolution.Rejected]: "Rejected by Internal",
};

export const getDisplayInfo = (
  resolution: ComplianceCaseResolution | string | null | undefined,
  status?: ComplianceCaseStatus | string | null,
  internal = false,
): DisplayInfo => {
  const info = getBaseDisplayInfo(resolution, status);
  const internalText = internal
    ? INTERNAL_DISPLAY_TEXT[resolution as ComplianceCaseResolution]
    : undefined;
  return internalText ? { ...info, displayText: internalText } : info;
};

const getBaseDisplayInfo = (
  resolution: ComplianceCaseResolution | string | null | undefined,
  status?: ComplianceCaseStatus | string | null,
): DisplayInfo => {
  if (isUnspecifiedResolution(resolution) && isInProgressStatus(status)) {
    return { displayText: "Assigned", iconType: "user" };
  }
  switch (resolution) {
    // Handle the Investor Onboarding case resolutions
    case ComplianceCaseResolution.IoPendingDataSubmission:
      return {
        displayText: "Missing data/docs",
        iconType: "file-edit",
      };
    case ComplianceCaseResolution.IoReadyResubmitted:
      return {
        displayText: "LP data/docs resubmitted",
        iconType: "file-download",
      };
    // All reopened cases (something we might be disabling) display the same
    case ComplianceCaseResolution.IoReadyReopened:
    case ComplianceCaseResolution.SofReadyReopened:
    case ComplianceCaseResolution.TmReadyReopened:
    case ComplianceCaseResolution.Reopened:
      return {
        displayText: "Re-opened case",
        iconType: "folder-open",
      };
    case ComplianceCaseResolution.IoReadyRestarted:
      return {
        displayText: "Data/doc change detected",
        iconType: "file-exclamation",
      };
    case ComplianceCaseResolution.IoReadyPendingFundEscalation:
      return {
        displayText: "Requires GP review",
        iconType: "exclamation-triangle",
      };
    case ComplianceCaseResolution.IoReadyPendingResubmission:
    case ComplianceCaseResolution.PendingResubmission:
      return {
        displayText: "Requires LP data/doc submission",
        iconType: "file-edit",
      };
    case ComplianceCaseResolution.TmReadyEscalated:
      return {
        displayText: "Requires CO review",
        iconType: "exclamation-triangle",
      };
    // All in progress cases display the same
    case ComplianceCaseResolution.IoInProgress:
    case ComplianceCaseResolution.SofInProgress:
    case ComplianceCaseResolution.TmInProgress:
      return {
        displayText: "Assigned",
        iconType: "user",
      };
    case ComplianceCaseResolution.IoInProgressDeEscalated:
    case ComplianceCaseResolution.DeEscalated:
      return {
        displayText: "De-escalated",
        iconType: "arrow-down",
      };
    // All returned to maker cases display the same
    case ComplianceCaseResolution.IoInProgressReturned:
    case ComplianceCaseResolution.SofInProgressReturned:
    case ComplianceCaseResolution.TmInProgressReturned:
      return {
        displayText: "Returned",
        iconType: "user",
      };
    case ComplianceCaseResolution.IoInProgressResubmitted:
      return {
        displayText: "LP data/docs resubmitted",
        iconType: "file-download",
      };
    case ComplianceCaseResolution.ProposedApproved:
      return {
        displayText: "Proposed Approved",
        iconType: "check-circle",
        iconColor: Colors.Success600,
      };
    case ComplianceCaseResolution.ProposedDenied:
      return {
        displayText: "Proposed Denied",
        iconType: "xmark",
        iconColor: Colors.Danger600,
      };
    case ComplianceCaseResolution.Denied:
      return {
        displayText: "Denied",
        iconType: "xmark",
        iconColor: Colors.Danger600,
      };
    case ComplianceCaseResolution.IoPendingReview:
    case ComplianceCaseResolution.IoUnderReview:
      return {
        displayText: "Proposed complete",
        iconType: "user-check",
      };
    case ComplianceCaseResolution.IoPendingReviewAccepted:
    case ComplianceCaseResolution.IoPendingReviewRejected:
    case ComplianceCaseResolution.IoUnderReviewAccepted:
    case ComplianceCaseResolution.IoUnderReviewRejected:
      return {
        displayText: "GP verdict rendered",
        iconType: "badge-check",
      };
    case ComplianceCaseResolution.IoPendingReviewProposedDeficient:
    case ComplianceCaseResolution.IoUnderReviewProposedDeficient:
    case ComplianceCaseResolution.ProposedDeficient:
      return {
        displayText: "Proposed LP data/doc collection",
        iconType: "user-magnifying-glass",
      };
    case ComplianceCaseResolution.IoPendingReviewProposedFundEscalation:
    case ComplianceCaseResolution.IoUnderReviewProposedFundEscalation:
    case ComplianceCaseResolution.ProposedEscalation:
      return {
        displayText: "Proposed GP escalation",
        iconType: "user-magnifying-glass",
      };
    case ComplianceCaseResolution.IoPendingReviewProposedInvalid:
    case ComplianceCaseResolution.IoUnderReviewProposedInvalid:
    case ComplianceCaseResolution.ProposedInvalid:
      return {
        displayText: "Proposed invalid",
        iconType: "ban",
      };
    case ComplianceCaseResolution.IoPendingReviewProposedWaived:
    case ComplianceCaseResolution.IoUnderReviewProposedWaived:
      return {
        displayText: "Proposed waived",
        iconType: "ban",
      };
    case ComplianceCaseResolution.IoPendingReviewProposedDeferred:
    case ComplianceCaseResolution.IoUnderReviewProposedDeferred:
      return {
        displayText: "Proposed deferred",
        iconType: "hourglass-clock",
      };
    case ComplianceCaseResolution.IoDeferred:
      return {
        displayText: "Compliance deferred",
        iconType: "hourglass-clock",
      };
    case ComplianceCaseResolution.IoPendingFundReview:
      return {
        displayText: "Escalated to GP",
        iconType: "exclamation-triangle",
        iconColor: Colors.Danger600,
      };
    case ComplianceCaseResolution.IoPendingDataResubmission:
      return {
        displayText: "LP request in progress",
        iconType: "file-edit",
      };
    case ComplianceCaseResolution.IoClosed:
    case ComplianceCaseResolution.Approved:
      return {
        displayText: "Approved",
        iconType: "check-circle",
        iconColor: Colors.Success600,
      };
    case ComplianceCaseResolution.IoClosedAccepted:
      return {
        displayText: "GP verdict: approved",
        iconType: "check-circle",
        iconColor: Colors.Success600,
      };
    case ComplianceCaseResolution.IoClosedRejected:
      return {
        displayText: "GP verdict: rejected",
        iconType: "xmark",
        iconColor: Colors.Danger600,
      };
    // All closed invalid cases display the same
    case ComplianceCaseResolution.IoClosedInvalid:
    case ComplianceCaseResolution.SofClosedInvalid:
    case ComplianceCaseResolution.TmClosedInvalid:
      return {
        displayText: "Invalidated",
        iconType: "ban",
      };
    case ComplianceCaseResolution.IoClosedWaived:
      return {
        displayText: "GP waived",
        iconType: "ban",
      };

    case ComplianceCaseResolution.IoClosedCanceled:
      return {
        // Only subscription services can cancel cases currently
        displayText: "Subscription Canceled",
        iconType: "ban",
        iconColor: Colors.Danger600,
      };
    // Handle the Source of Funds case resolutions
    case ComplianceCaseResolution.SofPendingReviewProposedAcceptance:
    case ComplianceCaseResolution.SofUnderReviewProposedAcceptance:
      return {
        displayText: "Proposed acceptance",
        iconType: "user-check",
      };
    case ComplianceCaseResolution.SofPendingReviewProposedRejection:
    case ComplianceCaseResolution.SofUnderReviewProposedRejection:
      return {
        displayText: "Proposed rejection",
        iconType: "user-magnifying-glass",
      };
    case ComplianceCaseResolution.SofPendingReviewProposedInvalid:
    case ComplianceCaseResolution.SofUnderReviewProposedInvalid:
    case ComplianceCaseResolution.TmPendingReviewProposedInvalid:
    case ComplianceCaseResolution.TmUnderReviewProposedInvalid:
      return {
        displayText: "Proposed invalidation",
        iconType: "ban",
      };
    case ComplianceCaseResolution.SofClosedApproved:
      return {
        displayText: "Accepted",
        iconType: "check-circle",
        iconColor: Colors.Success600,
      };
    case ComplianceCaseResolution.SofClosedRejected:
      return {
        displayText: "Rejected",
        iconType: "xmark",
        iconColor: Colors.Danger600,
      };
    // Handle the Transaction Monitoring case resolutions
    case ComplianceCaseResolution.TmPendingReviewProposedResolved:
    case ComplianceCaseResolution.TmUnderReviewProposedResolved:
      return {
        displayText: "Proposed resolved",
        iconType: "user-check",
      };
    case ComplianceCaseResolution.TmPendingReviewProposedEscalation:
    case ComplianceCaseResolution.TmUnderReviewProposedEscalation:
      return {
        displayText: "Proposed escalation",
        iconType: "user-magnifying-glass",
      };
    case ComplianceCaseResolution.TmPendingReviewDeEscalated:
    case ComplianceCaseResolution.TmUnderReviewDeEscalated:
      return {
        displayText: "Manually de-escalated",
        iconType: "arrow-down",
      };
    case ComplianceCaseResolution.TmPendingReviewCleared:
    case ComplianceCaseResolution.TmUnderReviewCleared:
      return {
        displayText: "CO verdict: resolved",
        iconType: "badge-check",
      };
    case ComplianceCaseResolution.TmPendingReviewHalted:
    case ComplianceCaseResolution.TmUnderReviewHalted:
      return {
        displayText: "CO verdict: halt",
        iconType: "badge-check",
      };
    case ComplianceCaseResolution.TmEscalated:
      return {
        displayText: "Escalated",
        iconType: "exclamation-triangle",
        iconColor: Colors.Danger600,
      };
    case ComplianceCaseResolution.TmClosedResolved:
      return {
        displayText: "Resolved",
        iconType: "check-circle",
        iconColor: Colors.Success600,
      };
    case ComplianceCaseResolution.TmClosedCleared:
      return {
        displayText: "CO verdict: resolved",
        iconType: "check-circle",
        iconColor: Colors.Success600,
      };
    case ComplianceCaseResolution.TmClosedHalted:
      return {
        displayText: "CO verdict: halt",
        iconType: "xmark",
        iconColor: Colors.Danger600,
      };
    case ComplianceCaseResolution.ProposedResolved:
      return {
        displayText: "Proposed complete",
        iconType: "user-check",
        iconColor: Colors.Success600,
      };
    case ComplianceCaseResolution.IoPendingReviewEscalated:
    case ComplianceCaseResolution.IoUnderReviewEscalated:
    case ComplianceCaseResolution.Escalated:
      return {
        displayText: "Escalated to GP",
        iconType: "arrow-up",
        iconColor: Colors.Danger600,
      };
    case ComplianceCaseResolution.Resolved:
      return {
        displayText: "No matches detected",
        iconType: "badge-check",
        iconColor: Colors.Success600,
      };
    case ComplianceCaseResolution.Invalid:
      return {
        displayText: "Invalidated",
      };
    case ComplianceCaseResolution.ProposedAcceptance:
    case ComplianceCaseResolution.ProposedRejection:
      return {
        displayText: "GP verdict rendered",
        iconType: "badge-check",
      };
    case ComplianceCaseResolution.Accepted:
      return {
        displayText: "GP verdict: approved",
        iconType: "check",
        iconColor: Colors.Success600,
      };
    case ComplianceCaseResolution.Rejected:
      return {
        displayText: "GP verdict: rejected",
        iconType: "xmark",
        iconColor: Colors.Danger600,
      };

    // Handle basic cases where the resolution is not defined
    case "":
    case ComplianceCaseResolution.IoPendingSubmission:
    case ComplianceCaseResolution.IoReady:
    case ComplianceCaseResolution.SofReady:
    case ComplianceCaseResolution.TmReady:
    case ComplianceCaseResolution.Unknown:
    default:
      return {
        displayText: "--",
      };
    case ComplianceCaseResolution.ReturnedToMaker:
      return {
        displayText: "Returned",
        iconType: "user",
      };
  }
};
